import { buildOperatorRig, type OperatorRigState, type RigPart } from "@common/utils/operatorRig";
import { CanvasSource, Graphics, Rectangle, Sprite, Texture } from "pixi.js";
import { PIXI_SCALE } from "./constants";
import { OperatorRaster } from "./operatorRaster";
import { RetainedOperatorRig } from "./retainedOperatorRig";
import { OperatorUploadQueue, sameOperatorPose } from "./operatorRenderState";
import { operatorPaintPreview } from "./operatorPaint";
import { FloorNames } from "@common/utils/terrain";
import { OperatorGroundContact } from "./operatorGroundContact";

interface OperatorAtlas { raster: OperatorRaster; source: CanvasSource; }
const atlases: OperatorAtlas[] = [];
const uploads = new OperatorUploadQueue();
function allocate(): { atlas: OperatorAtlas; slot: number } {
    for (const atlas of atlases) {
        try { return { atlas, slot: atlas.raster.acquire() }; } catch { /* Allocate a new page when full. */ }
    }
    const raster = new OperatorRaster();
    const atlas = { raster, source: new CanvasSource({ resource: raster.canvas, scaleMode: "linear", autoGenerateMipmaps: false }) };
    atlases.push(atlas);
    return { atlas, slot: raster.acquire() };
}

/** Dynamic projection of articulated meshes into the existing 2D world. */
export class OperatorVisual {
    static graphicsInfo() { return atlases.map(atlas => atlas.raster.contextInfo()); }
    /** Call after actor drawing, before Pixi renders the scene. */
    static flushUploads(): void { uploads.flush(); }
    private readonly rig: RetainedOperatorRig;
    private lastPose?: OperatorRigState;
    private readonly allocation = allocate();
    readonly body: Sprite;
    readonly foreground: Sprite;
    private readonly ground?: OperatorGroundContact;
    readonly shadow;
    readonly groundEffects;
    parts: RigPart[] = [];
    /** referenceGeometry is only used by the local A/B diagnostic. */
    constructor(private readonly referenceGeometry = false, painted = operatorPaintPreview(location.hostname, location.search), grounding = true) {
        this.ground = grounding ? new OperatorGroundContact() : undefined;
        this.shadow = this.ground?.shadow ?? new Graphics().ellipse(0, 46, 27, 10).fill({ color: 0x18231c, alpha: 0.23 });
        this.groundEffects = this.ground?.effects;
        this.rig = new RetainedOperatorRig(painted);
        const { atlas, slot } = this.allocation;
        const sprite = (front: boolean): Sprite => {
            const rect = atlas.raster.region(slot, front);
            const result = new Sprite(new Texture({ source: atlas.source, frame: new Rectangle(rect.x, rect.y, rect.width, rect.height) }));
            result.anchor.set(0.5);
            result.width = result.height = OperatorRaster.extent * 2 * PIXI_SCALE;
            result.zIndex = front ? 5 : 1;
            return result;
        };
        this.body = sprite(false); this.foreground = sprite(true);
        this.shadow.zIndex = -5;
    }
    draw(state: OperatorRigState, parentRotation: number, floor = FloorNames.Grass, x = 0, y = 0, layer = 0, now = performance.now()): void {
        this.body.rotation = this.foreground.rotation = this.shadow.rotation = -parentRotation;
        this.ground?.update(state, parentRotation, floor, x, y, layer, now);
        if (sameOperatorPose(this.lastPose, state)) return;
        this.parts = buildOperatorRig(state, true, false, this.referenceGeometry ? undefined : this.rig);
        const { atlas, slot } = this.allocation;
        atlas.raster.draw(this.parts, state.rotation, slot);
        if (this.referenceGeometry) atlas.source.update();
        else uploads.mark(atlas.source);
        this.lastPose = { ...state, leftHand: { ...state.leftHand }, rightHand: { ...state.rightHand } };
    }
    set visible(value: boolean) {
        this.body.visible = this.foreground.visible = this.shadow.visible = value;
        if (this.ground) this.ground.visible = value;
    }
    destroy(): void {
        this.allocation.atlas.raster.release(this.allocation.slot);
        this.body.destroy({ texture: true, textureSource: false });
        this.foreground.destroy({ texture: true, textureSource: false });
        if (this.ground) this.ground.destroy();
        else this.shadow.destroy();
    }
}
